import { z } from 'zod';
import { httpClient } from '@/shared/api/httpClient';
/** 用户基础信息响应结构，与后端 LoginResponseVO.UserInfoVO 对齐。 */
const authUserSchema = z.object({
    userId: z.number(),
    uid: z.string(),
    phone: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    nickname: z.string().nullable().optional(),
    head: z.string().nullable().optional(),
    state: z.number().nullable().optional(),
    addTime: z
        .union([z.string(), z.number(), z.array(z.number())])
        .nullable()
        .optional(),
});
/** 登录响应结构，包含会话令牌与用户信息。 */
const loginResponseSchema = z.object({
    token: z.string().min(1),
    user: authUserSchema,
});
/** 读取统一响应外壳中的业务数据载荷。 */
function readEnvelopeData(response) {
    const envelope = response.data;
    return envelope?.data;
}
/** 请求发送登录短信验证码，服务端有 60 秒冷却。 */
export async function requestLoginCaptcha(phone) {
    await httpClient.get('/user/fetch-captcha', { params: { phone, type: 3 } });
}
/** 请求发送密码设置短信验证码，与登录验证码相互隔离。 */
export async function requestPasswordCaptcha(phone) {
    await httpClient.get('/user/fetch-captcha', { params: { phone, type: 2 } });
}
/** 手机验证码登录，未注册手机号会自动注册。 */
export async function phoneLogin(input) {
    const response = await httpClient.post('/user/phone-login', input);
    return loginResponseSchema.parse(readEnvelopeData(response));
}
/** 手机号密码登录，仅限已设置密码的用户。 */
export async function phoneLoginByPwd(input) {
    const response = await httpClient.post('/user/phone-login-by-pwd', input);
    return loginResponseSchema.parse(readEnvelopeData(response));
}
/** 校验当前账号手机短信验证码后设置或更新密码。 */
export async function setPassword(input) {
    await httpClient.post('/user/set-password', input);
}
/**
 * 忘记密码：免登录凭手机号与密码重置验证码重置密码。
 *
 * 与 {@link setPassword} 的差别只在身份证明方式：本接口不要求登录态，
 * 手机号必须是已注册账号，验证码走密码重置场景（type=2）。
 */
export async function resetPassword(input) {
    await httpClient.post('/user/reset-password', input);
}
/** 登出当前会话。 */
export async function logout() {
    await httpClient.post('/user/logout');
}
/** 查询当前登录用户信息，用于应用启动时的会话恢复。 */
export async function fetchCurrentUser() {
    const response = await httpClient.get('/user/current');
    return authUserSchema.parse(readEnvelopeData(response));
}
/** 上传头像文件，返回 AIGC 风格的头像 URL；资料保存由 updateUser 完成。 */
export async function uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const response = await httpClient.post('/user/uploadHead', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return z.string().min(1).parse(readEnvelopeData(response));
}
/** 按 AIGC 风格更新用户资料，调用方传入用户 ID 与需要更新的字段。 */
export async function updateUser(input) {
    const response = await httpClient.post('/user/update-user', input);
    return z.boolean().parse(readEnvelopeData(response));
}
